import stylesheet from './Tag.scss?inline';

import CustomComponent from "../../base/customComponent/CustomComponent";

type NXTSelectEvent = {
    name?: string; value?: string; checked?: boolean; unselectedOthers?: boolean;
};

export class NXTTag extends CustomComponent {
    static observedAttributes = ['color'];

    tagElement: HTMLSpanElement;

    constructor() {
        super();
        this.customStyle = stylesheet;
    }

    get color(): string {
        return this.getAttribute('color') ?? 'black';
    }

    get size(): string {
        return this.getAttribute('size') ?? 'md'
    }

    get leftDot(): string | null {
        return this.getAttribute('left-dot') ?? null;
    }

    get inputElement(): HTMLInputElement | undefined {
        return this.getSlotElements('input')[0] as HTMLInputElement;
    }

    get isRadio(): boolean {
        return this.inputElement?.type === 'radio';
    }

    get isCheckbox(): boolean {
        return this.inputElement?.type === 'checkbox';
    }

    get isSelected(): boolean {
        return this.inputElement?.checked ?? false;
    }

    getTagBreakpointSizeClasses(): string[] {
        const additionalClasses: string[] = [];
        for (const breakpoint of ['md', 'lg', 'xl', 'xxl']) {
            const attributeName = `size-${breakpoint}`;
            if (this.hasAttribute(attributeName)) {
                additionalClasses.push(`tag-${this.getAttribute(attributeName)}-${breakpoint}-up`);
            }
        }
        return additionalClasses;
    }

    // noinspection JSUnusedGlobalSymbols
    attributeChangedCallback(attributeName: string, oldValue: string, newValue: string) {
        if (this.tagElement && attributeName === 'color') {
            this.tagElement.classList.remove(`tag-${oldValue ?? 'black'}`);
            this.tagElement.classList.add(`tag-${newValue ?? 'black'}`);
        }
    }

    render() {
        this.tagElement = document.createElement('span');
        this.tagElement.classList.add('tag', `tag-${this.color}`, `tag-${this.size}`, ...this.getTagBreakpointSizeClasses(),);
        if (this.leftDot) {
            this.tagElement.classList.add('tag-left-dot');
            this.tagElement.style.setProperty('--left-dot-color', this.leftDot);
        }
        this.tagElement.innerHTML = `
            <slot name="input"></slot>
            <slot></slot>
        `;
        if (this.inputElement) {
            this.tagElement.classList.add('tag-selectable');
            this.tagElement.addEventListener('input', (ev: InputEvent) => {
                if (this.isCheckbox && this.inputElement?.value === '' && this.inputElement?.checked === false) {
                    ev.stopPropagation();
                    if (this.inputElement) {
                        this.inputElement.checked = true;
                    }
                }
                document.dispatchEvent(new CustomEvent<NXTSelectEvent>('nxt-tag-select', {
                    detail: {
                        name: this.inputElement?.name, value: this.inputElement?.value, checked: this.inputElement?.checked,
                    },
                }));
            });
            this.tagElement.addEventListener('click', () => {
                this.querySelector<HTMLLabelElement>('label')?.click();
            });
            document.addEventListener('nxt-tag-select', (ev: CustomEvent<NXTSelectEvent>) => {
                if (ev.detail.name !== this.inputElement?.name) {
                    return;
                }
                if (this.isRadio) {
                    if (ev.detail.value === this.inputElement?.value) {
                        this.tagElement.classList.add('selected');
                    } else {
                        this.tagElement.classList.remove('selected');
                    }
                } else if (this.isCheckbox) {
                    if (ev.detail.value === this.inputElement?.value) {
                        this.inputElement.checked = ev.detail.checked ?? true;
                    } else if ((ev.detail.value === '' && this.inputElement?.value !== '') || (ev.detail.value !== '' && this.inputElement?.value === '')) {
                        this.inputElement.checked = false;
                    }
                    this.tagElement.classList.toggle('selected', this.inputElement.checked);
                }
            });
            if (this.isSelected) {
                this.tagElement.classList.add('selected');
            }
        }
        return this.tagElement;
    }

}

customElements.define('nxt-tag', NXTTag);
